import { ART_PAL, ball, body, edge, gloss, shade, tint, type ArtPal, type ArtTheme } from './covers/kit';
import { cheeks, eye, smile, type AvatarFace } from './faceKit';
import { ZODIAC_FACES, ZODIAC_IDS, type ZodiacId } from './zodiac';

/*
 * Nine friendly geometric avatars (animal / robot faces), 128×128, circle-safe: everything is clipped to the
 * central circle (r = 62), so they can be shown round at any size. Ids match the server's avatar presets.
 * Zodiac faces live in art/zodiac.ts.
 */
export const AVATAR_ART_IDS = ['a01', 'a02', 'a03', 'a04', 'a05', 'a06', 'a07', 'a08', 'a09'] as const;
export type BaseAvatarId = (typeof AVATAR_ART_IDS)[number];
/** a01…a09 + zodiac z01…z12 (art/zodiac.ts) */
export type AvatarArtId = BaseAvatarId | ZodiacId;


const FACES: Record<BaseAvatarId, AvatarFace> = {
  // cat
  a01: {
    c: (P) => P.teal,
    draw: (P, p, c) =>
      `<path d="M30 60L34 24L56 44Z" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
      `<path d="M98 60L94 24L72 44Z" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
      `<ellipse cx="64" cy="76" rx="40" ry="34" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      eye(50, 72, 6, P) + eye(78, 72, 6, P) +
      `<path d="M60 84L68 84L64 89Z" fill="${P.pink}"/>` + smile(64, 92, 7, P) + cheeks(86, P),
  },
  // fox
  a02: {
    c: (P) => P.orange,
    draw: (P, p, c) =>
      `<path d="M26 66L30 22L58 46Z" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
      `<path d="M102 66L98 22L70 46Z" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
      `<path d="M24 62Q64 30 104 62Q96 104 64 112Q32 104 24 62Z" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
      `<path d="M30 74Q48 78 64 112Q80 78 98 74Q88 102 64 112Q40 102 30 74Z" fill="#FFFFFF" fill-opacity=".92"/>` +
      eye(50, 70, 5.5, P) + eye(78, 70, 5.5, P) + `<ellipse cx="64" cy="98" rx="6" ry="4.5" fill="${P.dark ? P.bg : '#111827'}"/>`,
  },
  // owl
  a03: {
    c: (P) => (P.dark ? P.gold : tint(P.gold, 0.35)),
    draw: (P, p, c) =>
      `<path d="M30 40L44 50M98 40L84 50" stroke="${edge(c, P)}" stroke-width="7" stroke-linecap="round"/>` +
      `<ellipse cx="64" cy="76" rx="40" ry="38" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<circle cx="48" cy="70" r="15" fill="#FFFFFF" stroke="${edge(c, P)}" stroke-width="2"/><circle cx="80" cy="70" r="15" fill="#FFFFFF" stroke="${edge(c, P)}" stroke-width="2"/>` +
      eye(48, 71, 7, P) + eye(80, 71, 7, P) +
      `<path d="M58 84L70 84L64 96Z" fill="${P.orange}" stroke="${edge(P.orange, P)}" stroke-width="1.5" stroke-linejoin="round"/>`,
  },
  // robot
  a04: {
    c: (P) => P.blue,
    draw: (P, p, c) =>
      `<path d="M64 36V22" stroke="${edge(c, P)}" stroke-width="4" stroke-linecap="round"/><circle cx="64" cy="20" r="6" fill="${P.orange}"/>` +
      `<rect x="26" y="36" width="76" height="66" rx="16" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<rect x="36" y="52" width="56" height="24" rx="12" fill="${P.dark ? P.bg : '#111827'}"/>` +
      `<circle cx="50" cy="64" r="6" fill="${P.teal}"/><circle cx="78" cy="64" r="6" fill="${P.teal}"/>` +
      `<rect x="48" y="84" width="32" height="7" rx="3.5" fill="#FFFFFF" fill-opacity=".8"/>` +
      `<rect x="18" y="58" width="8" height="20" rx="3" fill="${shade(c, 0.2)}"/><rect x="102" y="58" width="8" height="20" rx="3" fill="${shade(c, 0.2)}"/>`,
  },
  // bunny
  a05: {
    c: (P) => P.pink,
    draw: (P, p, c) =>
      `<ellipse cx="46" cy="30" rx="10" ry="26" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/><ellipse cx="46" cy="32" rx="4.5" ry="17" fill="#FFFFFF" fill-opacity=".6"/>` +
      `<ellipse cx="82" cy="30" rx="10" ry="26" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/><ellipse cx="82" cy="32" rx="4.5" ry="17" fill="#FFFFFF" fill-opacity=".6"/>` +
      `<circle cx="64" cy="80" r="36" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      eye(51, 76, 5.5, P) + eye(77, 76, 5.5, P) +
      `<ellipse cx="64" cy="88" rx="4.5" ry="3.5" fill="${P.dark ? P.bg : '#111827'}"/>` + smile(64, 94, 6, P),
  },
  // bear
  a06: {
    c: (P) => P.purple,
    draw: (P, p, c) =>
      `<circle cx="34" cy="44" r="14" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/><circle cx="94" cy="44" r="14" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<circle cx="64" cy="76" r="38" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<ellipse cx="64" cy="92" rx="18" ry="13" fill="#FFFFFF" fill-opacity=".85"/>` +
      eye(50, 70, 5.5, P) + eye(78, 70, 5.5, P) +
      `<ellipse cx="64" cy="87" rx="6" ry="4.5" fill="${P.dark ? P.bg : '#111827'}"/>` + smile(64, 95, 5, P),
  },
  // frog
  a07: {
    c: (P) => P.green,
    draw: (P, p, c) =>
      `<circle cx="42" cy="46" r="16" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/><circle cx="86" cy="46" r="16" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<ellipse cx="64" cy="80" rx="44" ry="32" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<circle cx="42" cy="46" r="9" fill="#FFFFFF"/><circle cx="86" cy="46" r="9" fill="#FFFFFF"/>` +
      eye(42, 47, 5, P) + eye(86, 47, 5, P) +
      `<path d="M40 88Q64 104 88 88" fill="none" stroke="${P.dark ? P.bg : '#111827'}" stroke-width="3.5" stroke-linecap="round"/>` + cheeks(84, P),
  },
  // penguin
  a08: {
    c: (P) => P.sky,
    draw: (P, p, c) =>
      `<ellipse cx="64" cy="74" rx="40" ry="40" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<path d="M34 80Q34 54 52 56Q64 62 76 56Q94 54 94 80Q90 108 64 110Q38 108 34 80Z" fill="#FFFFFF" fill-opacity=".95"/>` +
      eye(52, 72, 5.5, P) + eye(76, 72, 5.5, P) +
      `<path d="M56 84L72 84L64 94Z" fill="${P.orange}" stroke="${edge(P.orange, P)}" stroke-width="1.5" stroke-linejoin="round"/>`,
  },
  // one-eyed bot
  a09: {
    c: (P) => P.red,
    draw: (P, p, c) =>
      `<path d="M44 38L36 24M84 38L92 24" stroke="${edge(c, P)}" stroke-width="4" stroke-linecap="round"/><circle cx="36" cy="22" r="5" fill="${P.gold}"/><circle cx="92" cy="22" r="5" fill="${P.gold}"/>` +
      `<circle cx="64" cy="76" r="40" fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
      `<circle cx="64" cy="70" r="18" fill="#FFFFFF" stroke="${edge(c, P)}" stroke-width="2"/>` +
      eye(64, 71, 9, P) +
      `<rect x="48" y="96" width="32" height="6" rx="3" fill="#FFFFFF" fill-opacity=".75"/>`,
  },
};

const isZodiac = (id: AvatarArtId): id is ZodiacId => (ZODIAC_IDS as readonly string[]).includes(id);
const faceOf = (id: AvatarArtId): AvatarFace => (isZodiac(id) ? ZODIAC_FACES[id] : FACES[id]);

/** Avatar colour role (for rings / fallbacks). */
export function avatarAccent(id: AvatarArtId, theme: ArtTheme): string {
  return faceOf(id).c(ART_PAL[theme]);
}

export function avatarMarkup(id: AvatarArtId, P: ArtPal, p: string): string {
  const f = faceOf(id);
  const c = f.c(P);
  const bg = P.dark ? shade(c, 0.62) : tint(c, 0.8);
  return (
    `<defs><clipPath id="${p}k"><circle cx="64" cy="64" r="62"/></clipPath>${ball(`${p}b`, bg, 0.25, 0.2)}${body(`${p}h`, c, 0.4, 0.3, 0.5, 1)}</defs>` +
    `<g clip-path="url(#${p}k)">` +
    `<rect width="128" height="128" fill="url(#${p}b)"/>` +
    f.draw(P, p, c) +
    gloss(40, 36, 16, 6, -35, 0.25) +
    `</g>`
  );
}

export function avatarSvg(id: AvatarArtId, theme: ArtTheme, prefix = `av${id}${theme[0]}`): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="128" height="128">${avatarMarkup(id, ART_PAL[theme], prefix)}</svg>`;
}

export const avatarUri = (id: AvatarArtId, theme: ArtTheme) => 'data:image/svg+xml;utf8,' + encodeURIComponent(avatarSvg(id, theme));

export const isAvatarArt = (id: string): id is AvatarArtId =>
  (AVATAR_ART_IDS as readonly string[]).includes(id) || (ZODIAC_IDS as readonly string[]).includes(id);

/** Deterministic avatar for a numeric/string user key (for places that have no avatar id). */
export function avatarForKey(key: number | string): AvatarArtId {
  let h = 0;
  for (const ch of String(key)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_ART_IDS[h % AVATAR_ART_IDS.length] as AvatarArtId;
}
